import type {
	Subscriber,
	Experiments,
	IMaterializedStore,
	IMaterializedSubStore,
	StoreWithUpdate,
	StateRefsMap,
} from '@wix/thunderbolt-symbols'
import { AppMaterializerSymbol, ExperimentsSymbol, MaterializedStoreSymbol } from '@wix/thunderbolt-symbols'
import { getDisplayedId, getFullId, getTemplateItemId, getRefCompIdFromInflatedId } from '@wix/thunderbolt-commons'
import { createMaterializer } from '@wix/materializer'
import type { Materializer } from '@wix/thunderbolt-types'
import { withDependencies } from '@wix/thunderbolt-ioc'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

type Subscribers<T> = Array<Subscriber<T>>

export const getMaterializedStore = (materializer: Materializer, experiments: Experiments): IMaterializedStore => {
	const isPreviewRegionEnabled = isExperimentOpen(experiments, 'specs.thunderbolt.previewRegion')
	let observedRoots: Array<string> = []
	let subscribers: Subscribers<any> = []
	let generalStore: Record<string, any> = {}

	return {
		unmount: () => {
			materializer.cleanAll()
			observedRoots = []
			subscribers = []
			generalStore = {}
		},
		createContextBasedStore: <T extends Record<string, any>>(subStoreName: string): StoreWithUpdate<T> => {
			observedRoots.push(subStoreName)
			// Tracks contexts created via `setChildStore` (fragment seeding). An empty child store has no
			// keys for `materializer.get([subStoreName])` to surface, so without this set `getFragmentPageStoreName`
			// would route a fragment comp's first update to `general` instead of its (empty) fragment context.
			const registeredContexts = new Set<string>()

			const getLegacyPageStoreName = (compId: string, pageId?: string) => {
				const subStore = materializer.get([subStoreName]) || {}
				const storeNames = Object.keys(subStore)
				return (
					storeNames.find((storeName) => storeName !== 'general' && subStore[storeName][compId]) ||
					storeNames.find((storeName) => storeName !== 'general' && subStore[storeName][getFullId(compId)]) ||
					storeNames.find(
						(storeName) => storeName !== 'general' && subStore[storeName][getRefCompIdFromInflatedId(compId)]
					) ||
					(pageId && storeNames.find((storeName) => storeName !== 'general' && subStore[storeName][pageId])) ||
					'general'
				)
			}

			const getFragmentPageStoreName = (compId: string, fragmentId?: string) => {
				const subStore = materializer.get([subStoreName]) || {}
				const storeNames = Object.keys(subStore).filter((storeName) => storeName !== 'general')

				const fragmentStore =
					fragmentId &&
					(storeNames.find((storeName) => subStore[storeName][fragmentId]) ||
						storeNames.find((storeName) => storeName === fragmentId) ||
						(registeredContexts.has(fragmentId) ? fragmentId : undefined))

				const pageStore =
					storeNames.find((storeName) => subStore[storeName][compId]) ||
					storeNames.find((storeName) => subStore[storeName][getFullId(compId)]) ||
					storeNames.find((storeName) => subStore[storeName][getRefCompIdFromInflatedId(compId)])

				return fragmentStore || pageStore || 'general'
			}

			// `fragmentId` is either a `pageId` or a `fragmentInstance`
			const getPageStoreName = (compId: string, fragmentId?: string) => {
				return isPreviewRegionEnabled
					? getFragmentPageStoreName(compId, fragmentId)
					: getLegacyPageStoreName(compId, fragmentId)
			}

			const getContextIdOfCompId = (compId: string): string | null => {
				const pageStore = getPageStoreName(compId)
				return pageStore === 'general' ? null : pageStore
			}

			const update = <TSpecificData>(partialStore: TSpecificData) => {
				const invalidations = materializer.batch((materializerUpdate) => {
					for (const compId in partialStore) {
						const pageStore = getPageStoreName(compId)

						materializerUpdate(subStoreName, pageStore, compId, partialStore[compId])
						if (pageStore === 'general') {
							generalStore[compId] = partialStore[compId]
						}
					}
				})

				const updates = Object.assign(
					{},
					...invalidations.map((path) => {
						const [, , compId] = path
						return { [compId]: materializer.get(path) }
					})
				)

				subscribers.forEach((cb) => {
					cb(updates)
				})
			}

			const moveToPageStore = (id: string, fragmentId: string) => {
				if (!isPreviewRegionEnabled) {
					const currentStoreName = getPageStoreName(id, fragmentId)
					materializer.update(subStoreName, currentStoreName, id, undefined)
					const newStoreName = getPageStoreName(id, fragmentId)
					materializer.update(subStoreName, newStoreName, id, {})
					return
				}

				const currentStoreName = getPageStoreName(id)
				const newStoreName = getPageStoreName(id, fragmentId)

				if (currentStoreName === newStoreName) {
					return
				}

				const currentValue = materializer.get([subStoreName, currentStoreName, id])
				if (currentValue !== undefined) {
					materializer.update(subStoreName, currentStoreName, id, undefined)
				}
				if (currentStoreName === 'general') {
					delete generalStore[id]
				}
				materializer.update(subStoreName, newStoreName, id, currentValue || {})
			}

			const updatePageId = (id: string, pageId?: string) => {
				if (pageId && pageId !== id) {
					const currentStoreName = getPageStoreName(id)
					const currentStore = materializer.get([subStoreName, currentStoreName])

					if (!isPreviewRegionEnabled && !currentStore) {
						return
					}

					moveToPageStore(id, pageId)

					// repeater items with displayedIds inflated from 'id'
					const repeatersItems = Object.keys(currentStore || {}).filter(
						(compId) => getDisplayedId(id, getTemplateItemId(compId)) === compId
					)
					repeatersItems.forEach((inflatedId) => moveToPageStore(inflatedId, pageId))
				}
			}

			return {
				get: (id: string) => {
					const pageStore = getPageStoreName(id)!
					return materializer.get([subStoreName, pageStore, id])
				},
				getContextIdOfCompId,
				setChildStore: (contextId: string, pageNewStore?: T) => {
					if (pageNewStore) {
						if (isPreviewRegionEnabled) {
							registeredContexts.add(contextId)
						}
						const payload = Object.keys(generalStore).reduce<Record<string, Record<string, any>>>(
							(acc, compId) => {
								if (
									pageNewStore[compId] ||
									pageNewStore[getFullId(compId)] ||
									pageNewStore[getRefCompIdFromInflatedId(compId)]
								) {
									acc[contextId][compId] = { ...pageNewStore[compId], ...generalStore[compId] }
									acc.general[compId] = undefined
									delete generalStore[compId]
								}
								return acc
							},
							{ general: {}, [contextId]: {} }
						)

						const contextStore = { ...pageNewStore, ...payload[contextId] }
						materializer.batch((materializerUpdate) => {
							for (const generalKey in payload.general) {
								materializerUpdate(subStoreName, 'general', generalKey, payload.general[generalKey])
							}

							for (const contextKey in contextStore) {
								materializerUpdate(subStoreName, contextId, contextKey, contextStore[contextKey])
							}
						})
					} else {
						if (isPreviewRegionEnabled) {
							registeredContexts.delete(contextId)
						}
						const invalidations = materializer.update(subStoreName, contextId, undefined)
						const emptyStore = Object.assign({}, ...invalidations.map(([, , compId]) => ({ [compId]: null })))
						subscribers.forEach((cb) => cb(emptyStore))
					}
				},
				getEntireStore: () => {
					const { general, ...otherStores } = materializer.get(subStoreName)
					return Object.assign({}, general, ...Object.values(otherStores))
				},
				getAllKeys() {
					return new Set(Object.keys(this.getEntireStore()))
				},
				update,
				updatePageId,
				remove: () => {
					throw new Error('Unsupported')
				},
				set: () => {
					throw new Error('Unsupported')
				},
				subscribeToChanges: (cb: Subscriber<T>) => {
					subscribers.push(cb)
					return () => {
						const index = subscribers.indexOf(cb)
						if (index >= 0) {
							subscribers.splice(index, 1)
						}
					}
				},
			}
		},
		createStore: <T extends Record<string, any>>(subStoreName: string): IMaterializedSubStore<T> => {
			return {
				update: (partialStore: Partial<T>) => {
					const invalidations = materializer.batch((materializerUpdate) => {
						for (const key2 in partialStore) {
							for (const key3 in partialStore[key2]) {
								materializerUpdate(subStoreName, key2, key3, partialStore[key2][key3])
							}
						}
					})
					const updates = Object.assign(
						{},
						...invalidations.map((path) => {
							const [, , compId] = path
							return { [compId]: materializer.get(path) }
						})
					)

					subscribers.forEach((cb) => {
						cb(updates)
					})
				},
				get: (path: Array<string>) => {
					return materializer.get([subStoreName, ...path])
				},
			}
		},
	}
}

export const MaterializedStore = withDependencies([AppMaterializerSymbol, ExperimentsSymbol], getMaterializedStore)
export const AppMaterializer = withDependencies([], () => createMaterializer())
export const StateRefs = withDependencies([MaterializedStoreSymbol], (materializer: IMaterializedStore) =>
	materializer.createContextBasedStore<StateRefsMap>('stateRefs')
)
